"use client";

import { BookHeart, Bot, ChevronDown, ExternalLink, Gamepad2, HeartHandshake, Send } from "lucide-react";
import Link from "@/components/ui/Link";
import { Suspense, useEffect, useRef, useState } from "react";
import { Logo } from "@/components/ui/Logo";
import { useCoinsWaiting } from "@/lib/coins-badge";
import { FRAME_POINTER_EVENT } from "@/components/video/frame-media";
import { footerLabel, footerLinks, site } from "@/config/site";
import { ESCORTS_URL } from "@/config/escorts";
import { gameUrl } from "@/config/game";
import type { NavLink } from "@/lib/site-defaults";
import { cn } from "@/lib/cn";
import { useRandomVideo } from "@/components/video/RandomVideo";
import { BROWSE_NAV, MAIN_NAV, isActive, type NavItem, translated } from "./nav";
import { LanguageMenu } from "./LanguageMenu";
import { ThemeToggle } from "./ThemeToggle";
import { InstallRow } from "@/components/pwa/Install";
import { useLang } from "@/components/ui/lang-context";
import { useUi } from "@/components/ui/use-ui";
import { type Mode, MODES, modeOf } from "./modes";
import { useShell } from "./ShellProvider";
import { SidebarContext } from "./sidebar-context";
import { useSitePath } from "@/components/ui/use-site-path";

/**
 * beeg-style navigation: a slim icon rail on desktop that expands into a
 * frosted-glass drawer on hover; an off-canvas drawer on mobile.
 * At the top, a switch between what the site shows (Streams, Videos, VR, Cartoon, Hentai) also switches
 * what it holds: on the live cams pages the platforms and filters (`streams`, the home page's @sidebar
 * slot), everywhere else the site's navigation.
 */
/** `channel`: the Telegram channel's link, under the menu whichever it shows. */
export function Sidebar({ links, siteName, channel, streams }: { links: NavLink[]; siteName: string; channel: string | null; streams?: React.ReactNode }) {
  const { drawerOpen, setDrawerOpen } = useShell();
  const [hovered, setHovered] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const expanded = hovered || drawerOpen;
  const t = useUi();

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setDrawerOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen, setDrawerOpen]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // Leaving the rail straight onto a framed player fires no mouseleave, so the player says so.
  useEffect(() => {
    const leave = () => {
      window.clearTimeout(timer.current);
      setHovered(false);
    };
    window.addEventListener(FRAME_POINTER_EVENT, leave);
    return () => window.removeEventListener(FRAME_POINTER_EVENT, leave);
  }, []);

  const close = () => {
    window.clearTimeout(timer.current);
    setHovered(false);
    setDrawerOpen(false);
  };

  return (
    <>
      <div
        aria-hidden
        onClick={() => setDrawerOpen(false)}
        className={cn(
          "fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 md:hidden",
          drawerOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />
      <aside
        aria-label={t.nav.mainNavigation}
        onMouseEnter={() => {
          window.clearTimeout(timer.current);
          timer.current = window.setTimeout(() => setHovered(true), 110);
        }}
        onMouseLeave={() => {
          window.clearTimeout(timer.current);
          setHovered(false);
        }}
        onFocus={() => setHovered(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setHovered(false);
        }}
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-drawer flex-col overflow-x-hidden overflow-y-auto no-scrollbar",
          "transition-[width,transform,background-color,box-shadow] duration-300 ease-[cubic-bezier(.2,.8,.2,1)]",
          drawerOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
          expanded
            ? "glass border-y-0 border-l-0 shadow-[30px_0_80px_-40px_rgb(0_0_0/0.9)] md:w-drawer"
            : "border-r border-line bg-ink md:w-rail",
        )}
      >
        <div className="flex h-16 shrink-0 items-center px-5">
          {/* Its own width even in the rail (the rail clips it), so the hidden logo isn't squeezed out of shape. */}
          <div className={cn("shrink-0 transition-opacity duration-200", expanded ? "opacity-100" : "pointer-events-none opacity-0")}>
            <Logo height={30} />
          </div>
        </div>

        <SidebarContext.Provider value={{ expanded, onNavigate: close }}>
          <Suspense fallback={<SidebarBody pathname={null} expanded={expanded} links={links} onNavigate={close} streams={streams} />}>
            <ActiveSidebarBody expanded={expanded} links={links} onNavigate={close} streams={streams} />
          </Suspense>
        </SidebarContext.Provider>

        <div className="mt-3 px-2">
          <div className="mx-3 mb-3 border-t border-line" />
          {channel && (
            <NavRow
              item={{ href: channel, label: t.nav.telegram, icon: Send, external: true, own: true, stat: "telegram:menu" }}
              active={false}
              expanded={expanded}
              onNavigate={close}
            />
          )}
          {/* While the browser offers installing Sweet as an app (components/pwa). */}
          <InstallRow
            className={cn(NAV_ROW, "w-full text-soft hover:bg-white/[0.06] hover:text-fg")}
            label={(text) => <span className={cn("whitespace-nowrap transition-opacity duration-200", expanded ? "opacity-100" : "md:opacity-0")}>{text}</span>}
          />
        </div>

        <div
          className={cn(
            "mt-auto px-6 pb-6 pt-8 transition-opacity duration-200",
            expanded ? "opacity-100" : "pointer-events-none opacity-0",
          )}
        >
          <ThemeToggle className="mb-4" />
          <LanguageMenu className="mb-4" />
          <ul className="flex flex-wrap gap-x-3.5 gap-y-1.5 text-[11px] uppercase tracking-[0.08em] text-muted">
            {footerLinks.map((l) => (
              <li key={l.href}>
                <Link href={l.href} onClick={close} className="whitespace-nowrap hover:text-fg">
                  {footerLabel(l, t.footer)}
                </Link>
              </li>
            ))}
          </ul>
          <p className="mt-5 border-t border-line pt-5 text-[11px] uppercase tracking-[0.08em] text-faint">
            © {site.copyrightYear} {siteName}.
          </p>
        </div>
      </aside>
    </>
  );
}

type BodyProps = { expanded: boolean; links: NavLink[]; onNavigate: () => void; streams?: React.ReactNode };

function ActiveSidebarBody(props: BodyProps) {
  return <SidebarBody pathname={useSitePath()} {...props} />;
}

function SidebarBody({ pathname, streams, ...props }: BodyProps & { pathname: string | null }) {
  // The slot keeps its last content after leaving the live cams pages, so the page decides.
  const mode = modeOf(pathname);
  return (
    <>
      {/* A new kind of page closes the list (keyed). */}
      <ModeSwitch key={mode ?? ""} mode={mode} expanded={props.expanded} />
      {mode === "streams" ? streams : <NavLists pathname={pathname} {...props} />}
    </>
  );
}

/**
 * What the menu holds, and the site shows: one button with the current choice (Streams until a page says
 * otherwise) that opens the others: Videos, VR, Cartoon, Hentai (modes.ts). In the rail, the current
 * choice's icon, the others' under it when open. Switching keeps the menu open, so its new contents show.
 */
const MODE_ROW = "flex h-10 items-center rounded-lg text-[14px] text-soft transition-colors hover:bg-white/[0.08] hover:text-fg";

function ModeSwitch({ mode, expanded }: { mode: Mode | null; expanded: boolean }) {
  const t = useUi();
  const lang = useLang();
  const [open, setOpen] = useState(false);
  const current = MODES.find((m) => m.id === (mode ?? "streams"))!;
  const others = MODES.filter((m) => m.id !== current.id);
  const Icon = current.icon;
  return (
    <div className="mx-2 mt-1">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="mode-list"
        aria-label={`${t.nav.show}: ${t.nav[current.id]}`}
        title={t.nav[current.id]}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex w-full items-center rounded-xl font-medium transition-colors",
          expanded ? "h-12 gap-3 bg-white px-3.5 text-[15px] text-black hover:bg-white/90" : "h-10 gap-4 bg-white/10 px-[13px] text-[14px] text-fg",
        )}
      >
        <Icon size={expanded ? 20 : 22} strokeWidth={1.7} className={cn("shrink-0", !expanded && "text-brand-soft")} />
        <span className={cn("flex-1 truncate text-left", !expanded && "md:hidden")}>{t.nav[current.id]}</span>
        <ChevronDown size={18} strokeWidth={2} className={cn("shrink-0 transition-transform duration-200", open && "rotate-180", !expanded && "md:hidden")} />
      </button>
      {open && (
        <ul id="mode-list" className={cn("mt-1.5 flex flex-col gap-1", expanded && "rounded-xl bg-white/[0.04] p-1")}>
          {others.map(({ id, href, icon: ModeIcon }) => (
            <li key={id}>
              <Link href={href} title={t.nav[id]} onClick={() => setOpen(false)} className={cn(MODE_ROW, expanded ? "gap-3 px-2.5" : "gap-4 px-[13px]")}>
                <ModeIcon size={expanded ? 19 : 22} strokeWidth={1.7} className="shrink-0" />
                <span className={cn("truncate", !expanded && "md:hidden")}>{t.nav[id]}</span>
              </Link>
            </li>
          ))}
          {/* The sex stories (/stories), before the game. */}
          <li>
            <Link href="/stories" title={t.nav.stories} data-stat="stories:menu" onClick={() => setOpen(false)} className={cn(MODE_ROW, expanded ? "gap-3 px-2.5" : "gap-4 px-[13px]")}>
              <BookHeart size={expanded ? 19 : 22} strokeWidth={1.7} className="shrink-0" />
              <span className={cn("truncate", !expanded && "md:hidden")}>{t.nav.stories}</span>
            </Link>
          </li>
          {/* Sweet AI (/ai): AI girlfriends and generators, partners' (lib/offers). */}
          <li>
            <Link href="/ai" title={t.nav.ai} onClick={() => setOpen(false)} className={cn(MODE_ROW, expanded ? "gap-3 px-2.5" : "gap-4 px-[13px]")}>
              <Bot size={expanded ? 19 : 22} strokeWidth={1.7} className="shrink-0" />
              <span className={cn("truncate", !expanded && "md:hidden")}>{t.nav.ai}</span>
            </Link>
          </li>
          {/* The game, at its own address, last in the list. */}
          <li>
            <a href={gameUrl(lang)} target="_blank" rel="noopener" data-stat="game:menu" title={t.nav.game} onClick={() => setOpen(false)} className={cn(MODE_ROW, expanded ? "gap-3 px-2.5" : "gap-4 px-[13px]")}>
              <Gamepad2 size={expanded ? 19 : 22} strokeWidth={1.7} className="shrink-0" />
              <span className={cn("truncate", !expanded && "md:hidden")}>{t.nav.game}</span>
            </a>
          </li>
          {/* The escorts directory, at its own address (open in the countries it's offered in). */}
          <li>
            <a href={ESCORTS_URL} target="_blank" rel="noopener" data-stat="escorts:menu" title={t.nav.escorts} onClick={() => setOpen(false)} className={cn(MODE_ROW, expanded ? "gap-3 px-2.5" : "gap-4 px-[13px]")}>
              <HeartHandshake size={expanded ? 19 : 22} strokeWidth={1.7} className="shrink-0" />
              <span className={cn("truncate", !expanded && "md:hidden")}>{t.nav.escorts}</span>
            </a>
          </li>
        </ul>
      )}
    </div>
  );
}

function NavLists({
  pathname,
  expanded,
  links,
  onNavigate,
}: {
  pathname: string | null;
  expanded: boolean;
  links: NavLink[];
  onNavigate: () => void;
}) {
  const t = useUi();
  return (
    <nav className="flex flex-col gap-1 px-2 pt-2">
      {MAIN_NAV.map((item) => (
        <NavRow key={item.href} item={translated(item, t)} active={isActive(pathname, item.href)} expanded={expanded} onNavigate={onNavigate} />
      ))}
      {links.map((l) => (
        <NavRow key={l.url} item={{ href: l.url, label: l.label, icon: ExternalLink, external: true }} active={false} expanded={expanded} onNavigate={onNavigate} />
      ))}
      <div className="mx-3 my-3 border-t border-line" />
      {BROWSE_NAV.map((item) => (
        <NavRow key={item.href} item={translated(item, t)} active={isActive(pathname, item.href)} expanded={expanded} onNavigate={onNavigate} />
      ))}
    </nav>
  );
}

/** The pink dot (and, with the drawer open, a "+5" pill) while today's Sweet Coins are waiting. */
function CoinsBadge({ expanded }: { expanded: boolean }) {
  const coins = useCoinsWaiting();
  const t = useUi();
  if (!coins) return null;
  return (
    <>
      <span aria-hidden className="absolute left-[29px] top-2 grid size-2.5 place-items-center">
        <span className="absolute size-full animate-ping rounded-full bg-[#ff5fa2]/60" />
        <span className="relative size-2.5 rounded-full bg-[#ff5fa2] ring-2 ring-ink" />
      </span>
      <span
        className={cn(
          "ml-auto whitespace-nowrap rounded-full bg-[#ff5fa2]/15 px-2 py-0.5 text-[11.5px] font-medium text-[#ff5fa2] transition-opacity duration-200",
          expanded ? "opacity-100" : "md:opacity-0",
        )}
      >
        +{coins}
        <span className="sr-only"> {t.nav.coinsReady}</span>
      </span>
    </>
  );
}

const NAV_ROW = "relative flex h-11 shrink-0 items-center gap-4 rounded-xl px-[13px] text-[15px] transition-colors";

function NavRow({ item, active, expanded, onNavigate }: { item: NavItem; active: boolean; expanded: boolean; onNavigate: () => void }) {
  const Icon = item.icon;
  const className = cn(
    NAV_ROW,
    active ? "bg-white/10 text-fg" : "text-soft hover:bg-white/[0.06] hover:text-fg",
  );
  const body = (
    <>
      <Icon size={22} strokeWidth={1.6} className={cn("shrink-0 transition-colors", active && "text-brand-soft")} />
      <span className={cn("whitespace-nowrap transition-opacity duration-200", expanded ? "opacity-100" : "md:opacity-0")}>{item.label}</span>
      {item.badge === "coins" && <CoinsBadge expanded={expanded} />}
    </>
  );
  if (item.external) {
    return (
      <a href={item.href} target="_blank" rel={item.own ? "noopener" : "noopener sponsored"} data-stat={item.stat} title={item.label} className={className}>
        {body}
      </a>
    );
  }
  if (item.random) return <RandomRow item={item} className={className} onNavigate={onNavigate}>{body}</RandomRow>;
  return (
    <Link href={item.href} title={item.label} aria-current={active ? "page" : undefined} onClick={onNavigate} className={className}>
      {body}
    </Link>
  );
}

/** "Random video": picked without a reload; the address (/random) still works in a new tab. */
function RandomRow({ item, className, onNavigate, children }: { item: NavItem; className: string; onNavigate: () => void; children: React.ReactNode }) {
  const { go } = useRandomVideo();
  return (
    <Link
      href={item.href}
      prefetch={false}
      title={item.label}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        onNavigate();
        void go();
      }}
      className={className}
    >
      {children}
    </Link>
  );
}
